import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import ApiService from 'services/apiService';
import { StudentCountsByGradeRequest, StudentGroupBy } from 'generated/openApi';
import { StudentDashboardData, StudentFilter } from '../types';

interface StudentDataContextValue {
    data: StudentDashboardData | null;
    loading: boolean;
    error: string | null;

    filter: StudentFilter;
    updateFilter: (updates: Partial<StudentFilter>) => void;
    activeSchoolYear: number | null;
    schoolYears: number[];
}

const StudentDataContext = createContext<StudentDataContextValue | undefined>(undefined);

interface StudentDataProviderProps {
    children: React.ReactNode;
}

export const StudentDataProvider = ({ children }: StudentDataProviderProps) => {
    const [filter, setFilter] = useState<StudentFilter>({
        groupBy: StudentGroupBy.School,
        schoolYears: [],
    });

    const [data, setData] = useState<StudentDashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);

    const fetchData = useCallback(async (ignore: { current: boolean }) => {
        setLoading(true);
        setError(null);
        try {
            const request: StudentCountsByGradeRequest = {
                schoolYear: filter.groupBy !== StudentGroupBy.Year ? filter.schoolYear : undefined,
                programId: filter.programId,
                groupBy: filter.groupBy,
                schoolYears: filter.groupBy === StudentGroupBy.Year && filter.schoolYears.length > 0
                    ? filter.schoolYears
                    : undefined,
                schoolId: filter.schoolId,
                gradeLevelId: filter.gradeLevelId,
            };

            if (ignore.current) return;

            const response = await ApiService.home.getStudentCountsByGrade(request);

            setData({
                rows: (response.data ?? []).map(item => ({
                    groupId: item.groupId ?? 0,
                    groupName: item.groupName ?? '',
                    studentCount: item.studentCount ?? 0,
                    percentage: item.percentage ?? 0,
                    year: item.year,
                })),
                total: response.total ?? 0,
                schoolYear: response.schoolYear ?? 0,
                schoolYearDisplay: response.schoolYearDisplay ?? '',
                groupBy: filter.groupBy,
            });

            setActiveSchoolYear(prev => {
                if (prev === null && response.activeSchoolYear) {
                    return response.activeSchoolYear;
                }
                return prev;
            });
        } catch (err: any) {
            if (!ignore.current) {
                setError(err?.message || 'Failed to load student data');
            }
        } finally {
            if (!ignore.current) setLoading(false);
        }
    }, [filter.schoolYear, filter.programId, filter.groupBy, filter.schoolYears, filter.schoolId, filter.gradeLevelId]);

    useEffect(() => {
        const ignore = { current: false };
        fetchData(ignore);
        return () => { ignore.current = true; };
    }, [fetchData]);

    const updateFilter = useCallback((updates: Partial<StudentFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    const schoolYears: number[] = activeSchoolYear
        ? Array.from({ length: 6 }, (_, i) => activeSchoolYear - i)
        : [];

    return (
        <StudentDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                updateFilter,
                activeSchoolYear,
                schoolYears,
            }}
        >
            {children}
        </StudentDataContext.Provider>
    );
};

export const useStudentData = (): StudentDataContextValue => {
    const context = useContext(StudentDataContext);
    if (!context) {
        throw new Error('useStudentData must be used within StudentDataProvider');
    }
    return context;
};